import React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import DebouncedInput from './DebounceInput';
import Filter, { dateBetweenFilterFn } from './FilterComponent';
import fuzzyFilter from './Filters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import './Table.css';

/**
 * ServerTable
 *
 * Props:
 *  - columns: tanstack columns
 *  - fetchData: async ({ pageIndex, pageSize, sorting, columnFilters, globalFilter }) => { items, totalItems }
 *  - tableClass, containerClass, headerClass, filtersHeaderClass: styling hooks (same as your Table)
 *  - enableGlobalFilter: boolean
 *  - initialPageSize?: number (default 20)
 *  - fillAvailableHeight?: boolean (keeps pagination outside a two-axis table viewport)
 */
export default function ServerTable({
  columns,
  fetchData,
  tableClass = '',
  containerClass = 'table-responsive',
  headerClass = 'table-light',
  filtersHeaderClass = '',
  enableGlobalFilter = false,
  initialPageSize = 10,
  onRowDoubleClick,
  getRowClassName,
  onQueryStateChange,
  onBeforeQueryChange,
  fillAvailableHeight = false,
  globalFilterPlaceholder = 'Search all columns...',
  pageSizeOptions = [10, 20, 30, 40, 50, 100],
  refreshKey,
}) {
  const [data, setData] = React.useState([]);
  const [totalItems, setTotalItems] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [globalFilter, setGlobalFilter] = React.useState('');
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [sorting, setSorting] = React.useState([]);
  const [pagination, setPagination] = React.useState({ pageIndex: 0, pageSize: initialPageSize });
  const [filterResetKey, setFilterResetKey] = React.useState(0);
  const queryChangePendingRef = React.useRef(false);

  const requestQueryChange = React.useCallback(async (type, current, updater, apply, resetPage = false) => {
    const next = typeof updater === 'function' ? updater(current) : updater;
    if (JSON.stringify(next) === JSON.stringify(current) || queryChangePendingRef.current) return;

    queryChangePendingRef.current = true;
    let allowed = true;
    try {
      allowed = (await onBeforeQueryChange?.({ type, current, next })) !== false;
    } finally {
      queryChangePendingRef.current = false;
    }

    if (!allowed) {
      if (type === 'columnFilters' || type === 'globalFilter') setFilterResetKey((key) => key + 1);
      return;
    }

    apply(next);
    if (resetPage) setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [onBeforeQueryChange]);

  const pageCount = React.useMemo(
    () => Math.max(1, Math.ceil(totalItems / pagination.pageSize)),
    [totalItems, pagination.pageSize]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
      columnFilters,
      sorting,
      pagination,
    },
    manualPagination: true,
    pageCount,
    manualSorting: true,
    manualFiltering: true,
    // We still expose the same filterFns so your FilterComponent works,
    filterFns: {
      fuzzy: fuzzyFilter,
      dateBetweenFilterFn,
    },
    onGlobalFilterChange: (updater) => requestQueryChange('globalFilter', globalFilter, updater, setGlobalFilter, true),
    onColumnFiltersChange: (updater) => requestQueryChange('columnFilters', columnFilters, updater, setColumnFilters, true),
    onSortingChange: (updater) => requestQueryChange('sorting', sorting, updater, setSorting, true),
    onPaginationChange: (updater) => requestQueryChange('pagination', pagination, updater, setPagination),
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
  });

  // Fetch server data when table state changes
  React.useEffect(() => {
    let ignore = false;
    (async () => {
      const queryState = {
        pageIndex: table.getState().pagination.pageIndex,
        pageSize: table.getState().pagination.pageSize,
        sorting: table.getState().sorting,
        columnFilters: table.getState().columnFilters,
        globalFilter: table.getState().globalFilter,
      };
      onQueryStateChange?.(queryState);
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetchData(queryState);
        if (!ignore) {
          setData(res.items ?? []);
          setTotalItems(res.totalItems ?? 0);
        }
      } catch (err) {
        if (!ignore) setError(err);
        console.error('ServerTable fetch error:', err);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    })();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter, columnFilters, sorting, pagination.pageIndex, pagination.pageSize, fetchData, refreshKey]);

  const renderColumnHeaderRows = (rowClassName = '') => table.getHeaderGroups().map(headerGroup => (
    <tr key={`columns-${headerGroup.id}`} className={rowClassName}>
      {headerGroup.headers.map(header => (
        <th
          key={header.id}
          colSpan={header.colSpan}
          className="border-bottom-0 text-nowrap"
          style={header.column.columnDef?.meta?.headerStyle}
        >
          {header.isPlaceholder ? null : (
            <div
              className={
                header.column.getCanSort()
                  ? 'cursor-pointer select-none py-2 px-1'
                  : 'py-2 px-1'
              }
              onClick={header.column.getToggleSortingHandler()}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
              {{
                asc: <FontAwesomeIcon className="ms-2 text-accent2" icon={faSortUp} />,
                desc: <FontAwesomeIcon className="ms-2 text-accent2" icon={faSortDown} />,
              }[header.column.getIsSorted()] ??
                (header.column.getCanSort() ? (
                  <FontAwesomeIcon className="ms-2 text-accent2" icon={faSort} />
                ) : null)}
            </div>
          )}
        </th>
      ))}
    </tr>
  ));

  const renderFilterRows = (rowClassName = '') => table.getHeaderGroups().map(headerGroup => (
    <tr key={`filters-${headerGroup.id}`} className={rowClassName}>
      {headerGroup.headers.map(header => (
        <th
          key={header.id}
          colSpan={header.colSpan}
          style={header.column.columnDef?.meta?.filterStyle}
        >
          {header.isPlaceholder ? null : (
            header.column.getCanFilter() ? (
              <div>
                <Filter
                  key={`${header.column.id}-${filterResetKey}`}
                  column={header.column}
                  table={table}
                  onFilterApplied={() => table.setPageIndex(0)}
                />
              </div>
            ) : null
          )}
        </th>
      ))}
    </tr>
  ));

  const tableElement = (
    <table className={`table ${tableClass}`}>
      {fillAvailableHeight ? (
        <thead className="server-table-sticky-header">
          {renderColumnHeaderRows(headerClass)}
          {renderFilterRows(filtersHeaderClass)}
        </thead>
      ) : (
        <>
          <thead className={`${headerClass}`}>
            {renderColumnHeaderRows()}
          </thead>
          <thead className={`${filtersHeaderClass}`}>
            {renderFilterRows()}
          </thead>
        </>
      )}

      <tbody className="table-group-divider">
        {isLoading && (
          <tr>
            <td colSpan={columns.length}>
              <div className="py-4 text-center text-muted">Loading…</div>
            </td>
          </tr>
        )}
        {!isLoading && data.length === 0 && (
          <tr>
            <td colSpan={columns.length}>
              <div className="py-4 text-center text-muted">No records found</div>
            </td>
          </tr>
        )}
        {!isLoading &&
          table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              className={getRowClassName ? getRowClassName(row.original, row) : ''}
              onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row.original, row) : undefined}
              style={onRowDoubleClick ? { cursor: 'pointer' } : undefined}
            >
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} style={cell.column.columnDef?.meta?.cellStyle}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
      </tbody>
    </table>
  );

  const globalFilterControl = enableGlobalFilter && (
    <div>
      <DebouncedInput
        value={globalFilter ?? ''}
        onChange={value => {
          requestQueryChange('globalFilter', globalFilter, value, setGlobalFilter, true);
        }}
        className="p-2 mb-2 form-control form-control-sm"
        placeholder={globalFilterPlaceholder}
        aria-label={globalFilterPlaceholder}
      />
    </div>
  );

  const paginationElement = (
    <div
      className={`d-flex align-items-center gx-2 ${fillAvailableHeight ? 'server-table-pagination' : 'mt-3'}`}
    >
      <nav aria-label="Table pagination">
        <ul className={`pagination m-0 ${tableClass.includes('table-sm') ? 'pagination-sm' : ''}`}>
          <li className={`page-item ${pagination.pageIndex === 0 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => table.setPageIndex(0)} aria-label="First">
              «
            </button>
          </li>
          <li className={`page-item ${pagination.pageIndex === 0 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => table.previousPage()} aria-label="Previous">
              &lt;
            </button>
          </li>
          <li className={`page-item ${pagination.pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => table.nextPage()} aria-label="Next">
              &gt;
            </button>
          </li>
          <li className={`page-item ${pagination.pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => table.setPageIndex(pageCount - 1)} aria-label="Last">
              »
            </button>
          </li>
        </ul>
      </nav>

      <span className="d-flex align-items-center gx-1 ms-2">
        <div>Page</div>
        <strong className="ms-1">
          {pagination.pageIndex + 1} of {pageCount}
        </strong>
      </span>

      <span className="d-flex align-items-center gx-1 ms-2">
        | Go to page:
        <input
          type="number"
          min={1}
          value={pagination.pageIndex + 1}
          onChange={e => {
            const p = e.target.value ? Number(e.target.value) - 1 : 0;
            table.setPageIndex(Number.isNaN(p) ? 0 : p);
          }}
          className={`form-control w-auto ms-1 ${tableClass.includes('table-sm') ? 'form-control-sm' : ''}`}
        />
      </span>

      <select
        value={pagination.pageSize}
        onChange={e => table.setPageSize(Number(e.target.value))}
        className={`form-select w-auto ms-2 ${tableClass.includes('table-sm') ? 'form-select-sm' : ''}`}
      >
        {pageSizeOptions.map(ps => (
          <option key={ps} value={ps}>
            Show {ps}
          </option>
        ))}
      </select>

      {error && <span className="ms-3 text-danger small">Failed to load data.</span>}
    </div>
  );

  if (fillAvailableHeight) {
    return (
      <div className="server-table server-table-fill">
        {globalFilterControl && <div className="server-table-global-filter">{globalFilterControl}</div>}
        <div
          className={`w-100 ${containerClass} server-table-viewport`}
          role="region"
          aria-label="Scrollable table"
          tabIndex={0}
        >
          {tableElement}
        </div>
        {paginationElement}
      </div>
    );
  }

  return (
    <>
      <div className={`w-100 ${containerClass}`}>
        {globalFilterControl}
        <div className="h-2" />
        {tableElement}
      </div>
      {paginationElement}
    </>
  );
}
